import { useState, useCallback, useRef } from 'react';
import { ANCHOR_POINTS_MAP, TOTAL_IMAGES } from '../constants';

export const imagePath = (building, index) =>
  `/assets/images/img3/${building.toLowerCase()}/${building}_.${String(
    index
  ).padStart(4, '0')}.webp`;

export const useImagePreloader = () => {
  const [preloadState, setPreloadState] = useState({
    isLoading: false,
    progress: 0,
    loadedBuildings: new Set(),
    currentBuilding: '',
    error: null,
  });

  const preloadedImagesRef = useRef({});
  const loadedBuildingsRef = useRef(new Set());

  const preloadImage = useCallback((src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = src;
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to load: ${src}`));
    });
  }, []);

  const preloadBuilding = useCallback(
    async (building) => {
      // Skip if already loaded
      if (loadedBuildingsRef.current.has(building)) {
        console.log(`Building ${building} already preloaded`);
        return {
          loaded: TOTAL_IMAGES,
          failed: [],
          preloadedImages: preloadedImagesRef.current[building],
        };
      }

      setPreloadState((prev) => ({
        ...prev,
        isLoading: true,
        progress: 0,
        currentBuilding: building,
        error: null,
      }));

      try {
        const anchorPoints = ANCHOR_POINTS_MAP[building] || [];
        const loadedImages = {};
        const failedImages = [];
        let loadedCount = 0;

        // Initialize building images cache
        if (!preloadedImagesRef.current[building]) {
          preloadedImagesRef.current[building] = {};
        }

        // Step 1: Load first anchor point immediately for instant display
        const firstAnchor = anchorPoints[0];
        try {
          const img = await preloadImage(imagePath(building, firstAnchor));
          loadedImages[firstAnchor] = img;
          preloadedImagesRef.current[building][firstAnchor] = img;
          loadedCount++;

          setPreloadState((prev) => ({
            ...prev,
            progress: Math.round((loadedCount / TOTAL_IMAGES) * 100),
          }));
        } catch (error) {
          console.error(
            `Error loading first anchor image ${firstAnchor}:`,
            error
          );
          failedImages.push(firstAnchor);
        }

        // Step 2: Load remaining anchor points (priority)
        for (const index of anchorPoints) {
          if (index === firstAnchor) continue;
          try {
            const img = await preloadImage(imagePath(building, index));
            loadedImages[index] = img;
            preloadedImagesRef.current[building][index] = img;
            loadedCount++;

            const currentProgress = Math.round(
              (loadedCount / TOTAL_IMAGES) * 100
            );
            setPreloadState((prev) => ({
              ...prev,
              progress: currentProgress,
            }));
          } catch (error) {
            console.error(`Error loading anchor image ${index}:`, error);
            failedImages.push(index);
          }
        }

        // Step 3: Load all remaining images
        for (let i = 1; i <= TOTAL_IMAGES; i++) {
          if (anchorPoints.includes(i)) continue;
          try {
            const img = await preloadImage(imagePath(building, i));
            loadedImages[i] = img;
            preloadedImagesRef.current[building][i] = img;
            loadedCount++;

            const currentProgress = Math.round(
              (loadedCount / TOTAL_IMAGES) * 100
            );
            setPreloadState((prev) => ({
              ...prev,
              progress: currentProgress,
            }));
          } catch (error) {
            console.error(`Error loading image ${i}:`, error);
            failedImages.push(i);
          }
        }
        loadedBuildingsRef.current.add(building);
        setPreloadState((prev) => ({
          ...prev,
          isLoading: false,
          progress: 100,
          loadedBuildings: new Set(loadedBuildingsRef.current),
          error:
            failedImages.length > 0
              ? `${failedImages.length} images failed to load`
              : null,
        }));

        return {
          loaded: loadedCount,
          failed: failedImages,
          preloadedImages: loadedImages,
        };
      } catch (error) {
        setPreloadState((prev) => ({
          ...prev,
          isLoading: false,
          error: error.message || 'Failed to preload images',
        }));
        throw error;
      }
    },
    [preloadImage]
  );

  const preloadMultipleBuildings = useCallback(
    async (buildings) => {
      const results = [];
      for (const building of buildings) {
        const result = await preloadBuilding(building);
        results.push({ building, ...result });
      }
      return results;
    },
    [preloadBuilding]
  );

  const resetPreloadState = useCallback(() => {
    loadedBuildingsRef.current = new Set();
    setPreloadState({
      isLoading: false,
      progress: 0,
      loadedBuildings: new Set(),
      currentBuilding: '',
      error: null,
    });
    preloadedImagesRef.current = {};
  }, []);

  const getPreloadedImage = useCallback((building, index) => {
    return preloadedImagesRef.current[building]?.[index];
  }, []);

  const isLoaded = useCallback((building) => {
    return loadedBuildingsRef.current.has(building);
  }, []);

  return {
    ...preloadState,
    preloadBuilding,
    preloadMultipleBuildings,
    resetPreloadState,
    isLoaded,
    getPreloadedImage,
    preloadedImagesRef,
  };
};
